/**
 * useAuth - Supabase Auth state context + helper hook.
 *
 * Provider mountuje sie raz w App.tsx, subscribe na auth state changes.
 * Wszystkie strony uzywaja `useAuth()` zeby dostac { user, session, loading, signOut }.
 *
 * Loading state ma 2s safety timeout - gdy Supabase nie odpowie (mobile network flaky),
 * przelaczamy z loading=true na loading=false zeby nie blokowac UI w nieskonczonosc.
 */
import { createContext, useContext, useEffect, useRef, useState, useCallback, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import { isApiError } from "@adresflow-v2/shared";
import { REFERRAL_CODE_KEY } from "@/lib/referral";
import { recordReferral } from "@/lib/apiClient";
import { trackOnce, trackOncePerSession } from "@/lib/analytics";

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const LOADING_SAFETY_TIMEOUT_MS = 2000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Jedna próba zapisu polecenia na mount — onAuthStateChange potrafi strzelić
  // wielokrotnie (SIGNED_IN + TOKEN_REFRESHED), a POST nie ma być spamowany.
  const referralAttempted = useRef(false);

  useEffect(() => {
    let mounted = true;

    /** Polecenie zapisujemy TU, nie w AuthPage: kod musi przetrwać potwierdzenie
     *  maila otwarte w nowej karcie, gdzie /auth jest już odmontowany. */
    const flushReferral = async () => {
      if (referralAttempted.current) return;
      const code = localStorage.getItem(REFERRAL_CODE_KEY);
      if (!code) return;
      referralAttempted.current = true;
      const res = await recordReferral(code);
      // Kasujemy kod po ROZSTRZYGNIĘCIU: sukces albo TRWAŁE odrzucenie (zły kod,
      // własny kod). INTERNAL_ERROR/AUTH_MISSING/sieć zostawiają kod na kolejną
      // sesję — inaczej chwilowa awaria API cicho gubiłaby polecenie.
      const terminal = isApiError(res)
        ? res.code === "VALIDATION_ERROR" || res.code === "NOT_FOUND"
        : res.data.success;
      if (terminal) localStorage.removeItem(REFERRAL_CODE_KEY);
    };

    /** Konwersje sign_up/login. Metoda zgadywana z app_metadata.provider — dla
     *  rejestracji hasłem GoTrue ustawia "email", dla Google "google". */
    const trackAuthEvent = (u: User) => {
      const method = (u.app_metadata?.provider as string | undefined) ?? "email";
      const createdAt = u.created_at ? new Date(u.created_at).getTime() : 0;
      const lastSignInAt = u.last_sign_in_at ? new Date(u.last_sign_in_at).getTime() : 0;
      const isFirstSignIn =
        createdAt > 0 && lastSignInAt > 0 && Math.abs(lastSignInAt - createdAt) < 30_000;

      if (isFirstSignIn) {
        // Trwały dedupe per user: heurystyka „nowe konto" jest prawdziwa jeszcze
        // przez 30 s, a SIGNED_IN leci przy każdym powrocie do karty — bez tego
        // jedna rejestracja raportowałaby się wielokrotnie.
        trackOnce("sign_up", `signup_${u.id}`, { method });
      }
      trackOncePerSession("login", `login_${u.id}`, { method });
    };

    // Initial session check
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setUser(data.session?.user ?? null);
      setIsLoading(false);
      // Sentry user context - pomaga debug z konkretnym userId w error reports
      if (data.session?.user) {
        Sentry.setUser({ id: data.session.user.id, email: data.session.user.email });
        void flushReferral();
        trackAuthEvent(data.session.user);
      }
    });

    // Subscribe na zmiany (SIGNED_IN, SIGNED_OUT, TOKEN_REFRESHED)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mounted) return;
      setSession(newSession);
      setUser(newSession?.user ?? null);
      setIsLoading(false);
      if (event === "SIGNED_OUT" || !newSession?.user) {
        Sentry.setUser(null);
      } else if (newSession.user) {
        Sentry.setUser({ id: newSession.user.id, email: newSession.user.email });
        // setTimeout, NIE bezpośrednie wywołanie: flushReferral → fetchRailway →
        // getJwt → supabase.auth.getSession(), a wołanie metod klienta wewnątrz
        // callbacka onAuthStateChange blokuje navigatorLock (klient jest bez
        // `lock: null`). Objawiłoby się zawieszonym POST-em i zablokowanym
        // refreshem tokenu w tej karcie. Odroczenie o tick zwalnia lock.
        setTimeout(() => { void flushReferral(); }, 0);
        trackAuthEvent(newSession.user);
      }
    });

    // Safety timeout - gdy Supabase getSession() wisi dluzej niz 2s
    const safetyTimer = setTimeout(() => {
      if (mounted) setIsLoading(false);
    }, LOADING_SAFETY_TIMEOUT_MS);

    return () => {
      mounted = false;
      clearTimeout(safetyTimer);
      subscription.unsubscribe();
    };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    // Hard redirect - zapewnia czysty reset wszystkich stanu i auth-gates
    window.location.href = "/";
  }, []);

  const value: AuthContextValue = {
    user,
    session,
    isLoading,
    isAuthenticated: !!user,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
